import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { usePersistedState } from "@/hooks/usePersistedState";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { PageHeader } from "@/components/lembra/PageHeader";
import { PageShell } from "@/components/lembra/PageShell";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { toast } from "sonner";
import { cancelOpenOrdersForCelebration, assertCelebrationCanBeSkipped } from "@/lib/cancelCelebrationOrders";
import {
  format,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isSameDay,
  isToday,
  addMonths,
  subMonths,
  addYears,
  differenceInYears,
  getDay,
  isBefore,
  startOfDay,
  addDays,
} from "date-fns";
import { ChevronLeft, ChevronRight, CalendarDays, List, Users, Sparkles } from "lucide-react";
import { ErrorCard } from "@/components/lembra/ErrorCard";
import { safeFormat } from "@/lib/dates";
import { PageMeta } from "@/components/lembra/PageMeta";
// Sprint 2.5a /orders-new-redirect, Approve now routes to /chat
// with the celebration as initial brief instead of dropping the
// user into the legacy bakery cake-builder at /orders/new.
import { celebrationToChatUrl } from "@/lib/celebrationToChat";
import { useOpenAuthedConcierge } from "@/hooks/useOpenAuthedConcierge";
import {
  useProfile,
  useTeamMembers,
  useAllCelebrations,
  type CelebrationRecord,
  type TeamMemberRow,
} from "@/hooks/queries";

// Calendar joins team_members onto each celebration client-side so the
// hook can stay a single-table read + cache separately. This is the page-
// local shape that adds the joined member.
interface Celebration extends CelebrationRecord {
  member?: TeamMemberRow;
}

function ordinalSuffix(n: number) {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

// MS105: shared celebration-type → human label mapping. Mirrors MS103's
// labelForType in useNotificationGenerator + Dashboard's humanType so
// notifications, toasts, and badges all speak the same vocabulary
// across surfaces. Previously the Calendar used a two-branch ternary
// that silently mislabeled welcome / farewell / milestone / other as
// "work anniversary".
function celebrationLabel(type: string): string {
  switch (type) {
    case "birthday":
      return "birthday";
    case "work_anniversary":
      return "work anniversary";
    case "welcome":
      return "first day";
    case "farewell":
      return "farewell";
    case "milestone":
      return "milestone";
    default:
      return "celebration";
  }
}

// MS105: word-by-word title case for the badge slot ("first day"
// → "First Day"). Single-word labels stay sensible too.
function capitalize(s: string): string {
  return s.replace(/\b\w/g, (c) => c.toUpperCase());
}

function getInitials(name: string) {
  return name
    .split(" ")
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

export default function Calendar() {
  const { session } = useAuth();
  const navigate = useNavigate();
  const { openFromChatUrl, openGeneric } = useOpenAuthedConcierge();
  const queryClient = useQueryClient();

  const profileQuery = useProfile();
  const companyId = profileQuery.data?.company_id ?? null;

  const teamQuery = useTeamMembers(companyId);
  const celebrationsQuery = useAllCelebrations(companyId);

  // Only active members participate in celebration generation + display.
  const activeMembers = useMemo(
    () => (teamQuery.data ?? []).filter((m) => m.is_active),
    [teamQuery.data],
  );

  // Attach team-member data to each celebration row in a cheap lookup so
  // the render tree doesn't need to re-scan on every click.
  const celebrations = useMemo<Celebration[]>(() => {
    const memberMap = new Map(activeMembers.map((m) => [m.id, m]));
    return (celebrationsQuery.data ?? []).map((c) => ({
      ...c,
      member: memberMap.get(c.team_member_id),
    }));
  }, [celebrationsQuery.data, activeMembers]);

  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState<Date | null>(null);
  const [detailCelebration, setDetailCelebration] = useState<Celebration | null>(null);
  // MS69: persist filter across reloads. Was local useState that reset
  // to "all" on every visit — annoying for HR/EAs who default to one
  // celebration type. Same pattern Dashboard MS66 uses, same validate
  // safeguard for stale enum values.
  const [filter, setFilter] = usePersistedState<"all" | "birthday" | "work_anniversary">(
    "lembra:calendar-filter",
    "all",
    {
      validate: (v): v is "all" | "birthday" | "work_anniversary" =>
        v === "all" || v === "birthday" || v === "work_anniversary",
    },
  );

  // Auto-generate missing celebrations for the next 90 days whenever the
  // active members list changes. Fires once per companyId change, then
  // again if members are added/removed. We gate on a per-company ref (via
  // a sentinel prop on the query key) so navigating away and back doesn't
  // re-upsert on every mount.
  const [horizonUpserted, setHorizonUpserted] = useState<string | null>(null);

  useEffect(() => {
    if (!companyId) return;
    if (activeMembers.length === 0) return;
    const sentinel = `${companyId}:${activeMembers.length}:${activeMembers.map((m) => m.id).join(",")}`;
    if (horizonUpserted === sentinel) return;

    const today = startOfDay(new Date());
    const horizon = addDays(today, 90);
    const toUpsert: {
      company_id: string;
      team_member_id: string;
      type: string;
      date: string;
    }[] = [];

    for (const m of activeMembers) {
      // Members with no birthday on file (admin-seeded but unconfirmed)
      // can't generate a celebration, skip them. The missing-data widget
      // will surface them so the user can fill in the date.
      if (m.birthday) {
        const bday = new Date(m.birthday + "T00:00:00");
        let nextBday = new Date(today.getFullYear(), bday.getMonth(), bday.getDate());
        if (isBefore(nextBday, today)) nextBday = addYears(nextBday, 1);
        if (!isBefore(horizon, nextBday)) {
          toUpsert.push({
            company_id: companyId,
            team_member_id: m.id,
            type: "birthday",
            date: format(nextBday, "yyyy-MM-dd"),
          });
        }
      }

      if (m.start_date) {
        const start = new Date(m.start_date + "T00:00:00");
        if (isBefore(start, today)) {
          let nextAnni = new Date(today.getFullYear(), start.getMonth(), start.getDate());
          if (isBefore(nextAnni, today)) nextAnni = addYears(nextAnni, 1);
          if (!isBefore(horizon, nextAnni)) {
            toUpsert.push({
              company_id: companyId,
              team_member_id: m.id,
              type: "work_anniversary",
              date: format(nextAnni, "yyyy-MM-dd"),
            });
          }
        }
      }
    }

    if (toUpsert.length === 0) {
      setHorizonUpserted(sentinel);
      return;
    }

    supabase
      .from("celebrations")
      .upsert(toUpsert, { onConflict: "team_member_id,type,date" })
      .then(({ error }) => {
        // MS408: log upsert failures rather than swallowing them silently.
        // If the upsert fails (RLS violation, network drop, schema mismatch)
        // the calendar would appear to load normally but with no upcoming
        // celebrations generated. Logging surfaces the root cause in Sentry.
        if (error) {
          console.error("[Calendar] celebration horizon upsert failed:", error);
          return;
        }
        setHorizonUpserted(sentinel);
        queryClient.invalidateQueries({ queryKey: ["celebrations", companyId] });
      });
  }, [companyId, activeMembers, horizonUpserted, queryClient]);

  // Status update, optimistic cache patch + DB write + notification insert.
  async function updateStatus(cel: Celebration, status: "approved" | "skipped") {
    if (status === "skipped") {
      try {
        await assertCelebrationCanBeSkipped(cel.id);
      } catch (blockErr) {
        toast.error(
          blockErr instanceof Error
            ? blockErr.message
            : "Can't skip while a gift is in progress.",
        );
        return;
      }
    }

    // Optimistic: flip the cached row instantly so the UI reflects the
    // change without waiting on the round-trip. Invalidate on settle so
    // the source of truth reconciles.
    if (companyId) {
      queryClient.setQueryData<CelebrationRecord[]>(
        ["celebrations", companyId, "all"],
        (prev) => (prev ?? []).map((c) => (c.id === cel.id ? { ...c, status } : c)),
      );
    }

    const { error } = await supabase
      .from("celebrations")
      .update({ status })
      .eq("id", cel.id);

    if (error) {
      toast.error("That didn't save. Please try again.");
      // Roll back by invalidating; the refetch will restore the truth.
      if (companyId) {
        queryClient.invalidateQueries({ queryKey: ["celebrations", companyId, "all"] });
      }
      return;
    }

    if (status === "skipped") {
      try {
        await cancelOpenOrdersForCelebration(cel.id);
        queryClient.invalidateQueries({ queryKey: ["orders"] });
      } catch (orderErr) {
        console.warn("[Calendar] skip order cancel failed:", orderErr);
        toast.error(
          orderErr instanceof Error
            ? orderErr.message
            : "Couldn't cancel the linked order. Try from Orders.",
        );
        if (companyId) {
          queryClient.invalidateQueries({ queryKey: ["celebrations", companyId] });
        }
        return;
      }
    }

    if (companyId) {
      queryClient.invalidateQueries({ queryKey: ["celebrations", companyId] });
    }

    if (detailCelebration?.id === cel.id) {
      setDetailCelebration({ ...cel, status });
    }

    const memberName = cel.member?.name ?? "Team member";
    // MS105: was the same two-branch ternary as useNotificationGenerator
    // before MS103 — collapsed every welcome / farewell / milestone /
    // other into "work anniversary" in the notification + toast.
    // Reuses celebrationLabel() for the same coverage humanType has on
    // Dashboard (birthday / work anniversary / first day / farewell /
    // milestone / celebration fallback).
    const typeLabel = celebrationLabel(cel.type);
    // MS439: append T00:00:00 to force local-time parsing — same fix as
    // the other cel.date usages in this file (lines 366, 374, 379).
    const celDate = cel.date ? format(new Date(cel.date + "T00:00:00"), "d MMM") : "";

    if (status === "approved") {
      if (session?.user) {
        // MS416: capture error so a notification DB failure is logged rather
        // than silently swallowed. Primary flow (approval + navigation) still
        // proceeds — the notification is best-effort.
        const { error: notifErr } = await supabase.from("notifications").insert({
          user_id: session.user.id,
          title: `${memberName}'s ${typeLabel} approved`,
          message: `Great! Now create an order for ${memberName}'s celebration on ${celDate}.`,
          type: "celebration",
          related_id: cel.id,
          related_type: "celebration",
        });
        if (notifErr) {
          console.warn("[Calendar] approval notification insert failed:", notifErr.message);
        }
        queryClient.invalidateQueries({ queryKey: ["notifications", session.user.id] });
        window.dispatchEvent(new Event("lembra:notifications-read"));
      }
      toast.success(`${memberName}'s ${typeLabel} is approved.`);
      // Sprint 2.5a /orders-new-redirect, multi-vendor pivot
      // alignment. Was navigate(`/orders/new?celebration_id=${cel.id}`)
      // which dropped the user into the legacy bakery cake-builder
      // form. Now routes to /chat with the celebration pre-composed
      // as a natural-language brief + structured slots so the chat
      // concierge picks up where the customer is, not where the
      // pre-pivot form is.
      if (cel.member) {
        openFromChatUrl(celebrationToChatUrl(cel, cel.member));
      } else {
        // Defensive fallback when the joined member resolved to
        // undefined (rare, only happens during the brief window
        // between approval + the cache invalidate). /calendar is
        // a safe non-destructive landing.
        navigate("/calendar");
      }
    } else {
      if (session?.user) {
        // MS416: same best-effort logging pattern as the approval branch.
        const { error: notifErr } = await supabase.from("notifications").insert({
          user_id: session.user.id,
          title: `${memberName}'s ${typeLabel} skipped`,
          message: "This celebration has been skipped. You can change this in the calendar.",
          type: "celebration",
          related_id: cel.id,
          related_type: "celebration",
        });
        if (notifErr) {
          console.warn("[Calendar] skip notification insert failed:", notifErr.message);
        }
        queryClient.invalidateQueries({ queryKey: ["notifications", session.user.id] });
        window.dispatchEvent(new Event("lembra:notifications-read"));
      }
      toast(`${memberName}'s ${typeLabel} is skipped. You can change this anytime.`);
    }
  }

  // Calendar grid helpers
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });
  const startDayOfWeek = getDay(monthStart);

  const filteredCelebrations = useMemo(() => {
    if (filter === "all") return celebrations;
    return celebrations.filter((c) => c.type === filter);
  }, [celebrations, filter]);

  const celebrationsByDate = useMemo(() => {
    const map = new Map<string, Celebration[]>();
    for (const c of filteredCelebrations) {
      const key = c.date;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(c);
    }
    return map;
  }, [filteredCelebrations]);

  const groupedByMonth = useMemo(() => {
    const groups = new Map<string, Celebration[]>();
    // safeFormat protects against a celebration row with a missing or
    // malformed date, without it, `new Date("nullT00:00:00")`
    // produces Invalid Date and `format(...)` throws, taking down the
    // whole calendar render. Bucket those rows under "Unscheduled" so
    // the user can at least see them and edit.
    for (const c of filteredCelebrations) {
      const key = safeFormat(c.date, "MMMM yyyy", "Unscheduled");
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(c);
    }
    return Array.from(groups.entries());
  }, [filteredCelebrations]);

  function anniversaryYears(cel: Celebration) {
    if (cel.type !== "work_anniversary" || !cel.member?.start_date) return null;
    const start = new Date(cel.member.start_date + "T00:00:00");
    const celDate = new Date(cel.date + "T00:00:00");
    return differenceInYears(celDate, start);
  }

  function isMissed(cel: Celebration): boolean {
    if (cel.status !== "upcoming") return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return new Date(cel.date + "T00:00:00") < today;
  }

  function renderCelebrationCard(cel: Celebration, showActions = true) {
    const years = anniversaryYears(cel);
    const celDate = new Date(cel.date + "T00:00:00");
    const isSkipped = cel.status === "skipped";
    const missed = isMissed(cel);

    return (
      <div
        key={cel.id}
        className={`rounded-xl bg-white p-4 shadow-sm transition-all ${isSkipped ? "opacity-50" : ""} ${missed ? "ring-1 ring-coral/40" : ""}`}
      >
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-navy text-sm font-bold text-white">
            {cel.member ? getInitials(cel.member.name) : "?"}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span
                className="font-bold text-navy cursor-pointer hover:underline"
                onClick={() => setDetailCelebration(cel)}
              >
                {cel.member?.name ?? "Unknown"}
              </span>
              {/* MS105: badge title was also the two-branch ternary —
                  a welcome celebration showed a "Work Anniversary"
                  badge. Same celebrationLabel() helper, capitalised
                  for badge styling. */}
              <Badge variant={cel.type === "birthday" ? "coral" : "sky"}>
                {capitalize(celebrationLabel(cel.type))}
              </Badge>
              {/* Sprint 2.5a /calendar-fix, Approved was sharing the
                  coral variant with Missed, two semantically opposite
                  states reading visually identical (urgent-bad vs
                  complete-good). Approved → navy (the "locked in /
                  committed" treatment used elsewhere e.g. /team's
                  Autopilot badge). Missed stays coral as the only
                  urgent-attention state. */}
              <Badge
                variant={
                  missed
                    ? "coral"
                    : cel.status === "approved"
                    ? "navy"
                    : cel.status === "skipped"
                    ? "outline"
                    : "sky"
                }
              >
                {missed ? "Missed" : cel.status.charAt(0).toUpperCase() + cel.status.slice(1)}
              </Badge>
            </div>
            <p className="text-sm text-charcoal/70 mt-1">
              {format(celDate, "EEEE, d MMMM yyyy")}
            </p>
            {years != null && years > 0 && (
              <p className="text-sm text-charcoal/70">
                {ordinalSuffix(years)} work anniversary
              </p>
            )}
          </div>
          {showActions && cel.status === "upcoming" && !missed && (
            <div className="flex gap-2 shrink-0">
              <Button
                variant="primary"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  updateStatus(cel, "approved");
                }}
              >
                Approve
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  updateStatus(cel, "skipped");
                }}
              >
                Skip
              </Button>
            </div>
          )}
          {cel.status === "approved" && (
            <Button
              variant="ghost"
              size="sm"
              className="shrink-0 text-coral"
              onClick={() => {
                // MS65: was navigate(`/orders?celebration_id=${cel.id}`)
                // which silently dropped the param (Orders.tsx only reads
                // ?tab=). The button landed users on the unfiltered
                // orders list. Route to /chat with the celebration pre-
                // composed, matching the post-approval flow at line 236.
                if (cel.member) {
                  openFromChatUrl(celebrationToChatUrl(cel, cel.member));
                } else {
                  // Same defensive fallback as updateStatus above.
                  openGeneric();
                }
              }}
            >
              Create order →
            </Button>
          )}
        </div>
      </div>
    );
  }

  const loading =
    profileQuery.isLoading ||
    (Boolean(companyId) && (teamQuery.isLoading || celebrationsQuery.isLoading));

  if (loading) {
    return (
      // Sprint 2.5a /calendar-fix, aria-live + visible message.
      // Same accessibility pattern as /team-fix and the B.2 P1-5
      // loading states across /orders + /orders/:id.
      <div
        role="status"
        aria-live="polite"
        className="flex min-h-[50vh] flex-col items-center justify-center gap-3"
      >
        <div
          className="h-8 w-8 animate-spin rounded-full border-4 border-navy border-t-transparent"
          aria-hidden
        />
        <p className="text-sm text-charcoal/60">Loading your calendar…</p>
      </div>
    );
  }

  if (activeMembers.length === 0) {
    return (
      // MS253: empty state also wrapped so the header gets inset.
      <PageShell width="wide">
        <PageHeader
          title="Calendar"
          emphasis="Calendar"
          subtitle="Birthdays and work anniversaries, in one calm view."
        />
        {/* Sprint 2.5a /calendar-fix, warmer empty state mirroring
            the /team-fix pattern. The visitor lands here when they
            have no team members yet (or all members lack birthday +
            start_date). Sparkles icon matches /team-fix + /onboarding
            step-3 so the journey reads continuous; Newsreader italic
            heading; copy frames the multi-vendor outcome ("we'll
            stage the gift, the card, the timing"); CTA uses the
            same "Add my first team member" verbatim as /team so the
            verb sequence is predictable. */}
        <div
          role="region"
          aria-label="Empty calendar state"
          className="flex flex-col items-center justify-center py-20 text-center"
        >
          <div className="mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-butter/40">
            <Sparkles className="h-8 w-8 text-navy" />
          </div>
          <h2
            className="text-2xl font-bold text-navy mb-2"
            style={{ fontFamily: "Newsreader, Georgia, serif", fontStyle: "italic" }}
          >
            Your calendar fills itself.
          </h2>
          <p className="text-charcoal/70 mb-6 max-w-md">
            Add a team member with a birthday or start date and Lembra schedules the celebration, we'll stage the gift, the card, and the timing.
          </p>
          <Button variant="primary" size="lg" onClick={() => navigate("/team")}>
            <Users className="mr-2 h-4 w-4" /> Add my first team member
          </Button>
        </div>
      </PageShell>
    );
  }

  return (
    // MS253: PageShell — wide variant for the calendar grid + list.
    <PageShell width="wide">
      {/* MS408: noindex — /calendar is an authed-only internal surface.
          Without this a crawler reaching the shell would inherit the
          site-default meta and index the page. */}
      <PageMeta
        title="Calendar · Lembra"
        description="Your team's upcoming celebrations, automatically tracked."
        path="/calendar"
        noindex
      />
      <PageHeader
        title="Calendar"
        emphasis="Calendar"
        subtitle={`${celebrations.filter((c) => c.status !== "skipped").length} upcoming celebration${celebrations.filter((c) => c.status !== "skipped").length === 1 ? "" : "s"}, sorted by what's next.`}
      />

      {celebrationsQuery.isError && (
        <div className="mb-6">
          <ErrorCard
            title="Couldn't load your calendar"
            onRetry={() => celebrationsQuery.refetch()}
          />
        </div>
      )}

      <Tabs defaultValue="calendar" className="w-full">
        <TabsList className="mb-6 bg-sky/20">
          <TabsTrigger value="calendar" className="data-[state=active]:bg-sky data-[state=active]:text-navy">
            <CalendarDays className="mr-2 h-4 w-4" /> Calendar
          </TabsTrigger>
          <TabsTrigger value="list" className="data-[state=active]:bg-sky data-[state=active]:text-navy">
            <List className="mr-2 h-4 w-4" /> List
          </TabsTrigger>
        </TabsList>

        {/* Sprint 2.5a /calendar-fix, group role + aria-label so the
            filter cluster announces as a unit. Each button gets
            aria-pressed so the active state reads to screen readers
            (the variant swap was the only visual signal previously). */}
        <div
          role="group"
          aria-label="Filter celebrations by type"
          className="flex gap-2 mb-6"
        >
          {([["all", "All"], ["birthday", "Birthdays"], ["work_anniversary", "Anniversaries"]] as const).map(([val, label]) => (
            <Button
              key={val}
              variant={filter === val ? "secondary" : "ghost"}
              size="sm"
              onClick={() => setFilter(val)}
              aria-pressed={filter === val}
              className={filter === val ? "" : "text-charcoal/70"}
            >
              {label}
            </Button>
          ))}
        </div>

        {/* ---- CALENDAR VIEW ---- */}
        <TabsContent value="calendar">
          {/* MS38: month rail in Heidi typography. Newsreader italic
              on the year keeps the headline calm without competing
              with the grid below. The chevron buttons stay as ghost
              icons. */}
          <div className="flex items-center justify-between mb-4">
            <Button variant="ghost" size="icon" aria-label="Previous month" onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}>
              <ChevronLeft className="h-5 w-5 text-navy" />
            </Button>
            <h2
              style={{
                fontFamily: "Newsreader, Georgia, serif",
                fontWeight: 500,
                fontSize: "clamp(1.4rem, 2.4vw, 1.65rem)",
                lineHeight: 1.15,
                letterSpacing: "-0.012em",
                color: "#1D3461",
                margin: 0,
              }}
            >
              {format(currentMonth, "MMMM")}{" "}
              <em style={{ fontStyle: "italic", color: "#C4564A" }}>
                {format(currentMonth, "yyyy")}
              </em>
            </h2>
            <Button variant="ghost" size="icon" aria-label="Next month" onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}>
              <ChevronRight className="h-5 w-5 text-navy" />
            </Button>
          </div>

          <div className="grid grid-cols-7 text-center mb-0">
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
              <div key={d} className="text-sm font-bold text-navy py-2 border border-charcoal/10 bg-cream">
                {d}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7">
            {Array.from({ length: startDayOfWeek }).map((_, i) => (
              <div key={`empty-${i}`} className="h-28 bg-cream border border-charcoal/10" />
            ))}
            {daysInMonth.map((day) => {
              const key = format(day, "yyyy-MM-dd");
              const dayCels = celebrationsByDate.get(key) ?? [];
              const today_ = isToday(day);
              const isSelected = selectedDay && isSameDay(day, selectedDay);

              const getFirstName = (name: string) => name.split(" ")[0];
              // MS106: compact label for the month-grid chip. Same
              // celebration-type coverage as MS105's celebrationLabel,
              // but truncated for the tight cell width: "Anniv." not
              // "Work Anniversary", "1st Day" not "First Day". Falls
              // back to capitalised type for any unknown enum value.
              const getCelLabel = (c: Celebration) => {
                const firstName = c.member ? getFirstName(c.member.name) : "?";
                const compactType =
                  c.type === "birthday"
                    ? "Birthday"
                    : c.type === "work_anniversary"
                      ? "Anniv."
                      : c.type === "welcome"
                        ? "1st Day"
                        : c.type === "farewell"
                          ? "Farewell"
                          : c.type === "milestone"
                            ? "Milestone"
                            : "Celebration";
                return `${firstName}'s ${compactType}`;
              };

              const cellContent = (
                <div
                  className={`h-28 p-1.5 cursor-pointer transition-all border border-charcoal/10 overflow-hidden ${
                    today_
                      ? "bg-butter/40"
                      : isSelected
                      ? "bg-sky/15"
                      : "bg-cream hover:bg-white"
                  }`}
                >
                  <span className={`text-sm ${today_ ? "font-bold text-navy" : "font-medium text-navy"}`}>
                    {format(day, "d")}
                  </span>

                  {dayCels.length > 0 && (
                    <div className="hidden md:flex flex-col gap-0.5 mt-0.5">
                      {dayCels.slice(0, dayCels.length > 2 ? 1 : 2).map((c) => (
                        <span
                          key={c.id}
                          className={`text-[12px] leading-tight rounded-full px-1.5 py-0.5 truncate max-w-full block ${
                            c.type === "birthday"
                              ? "bg-butter text-navy font-medium"
                              : "bg-sky text-navy font-medium"
                          }`}
                        >
                          {getCelLabel(c)}
                        </span>
                      ))}
                      {dayCels.length > 2 && (
                        <span className="text-[11px] text-navy/70 pl-1">+{dayCels.length - 1} more</span>
                      )}
                    </div>
                  )}

                  {dayCels.length > 0 && (
                    <div className="flex md:hidden gap-1 mt-1 items-center flex-wrap">
                      {dayCels.length <= 2 ? (
                        dayCels.map((c) => (
                          <div
                            key={c.id}
                            className={`h-2.5 w-2.5 rounded-full ${
                              c.type === "birthday" ? "bg-butter" : "bg-sky"
                            }`}
                          />
                        ))
                      ) : (
                        <>
                          {/* One dot shown, so the count reads as the
                              hidden remainder (+N), matching the desktop
                              "+N more" chip on the same day. Previously
                              this showed the total, so the same day read
                              "+2 more" on desktop but "3" on mobile. */}
                          <div className="h-2.5 w-2.5 rounded-full bg-butter" />
                          <span className="text-[11px] text-navy/70 font-medium">+{dayCels.length - 1}</span>
                        </>
                      )}
                    </div>
                  )}
                </div>
              );

              if (dayCels.length > 0) {
                return (
                  <Popover key={key}>
                    <PopoverTrigger asChild>
                      <div onClick={() => setSelectedDay(day)}>
                        {cellContent}
                      </div>
                    </PopoverTrigger>
                    <PopoverContent
                      className="w-80 p-0 rounded-xl shadow-xl border border-charcoal/10"
                      side="right"
                      sideOffset={8}
                      align="start"
                      collisionPadding={16}
                    >
                      <div className="bg-navy px-4 py-3 rounded-t-xl">
                        <p className="text-white font-bold text-sm">
                          {format(day, "EEEE, d MMMM")}
                        </p>
                        <p className="text-white/60 text-xs">
                          {dayCels.length} celebration{dayCels.length > 1 ? "s" : ""}
                        </p>
                      </div>
                      <div className="p-3 space-y-2 max-h-64 overflow-y-auto">
                        {dayCels.map((c) => {
                          const years = anniversaryYears(c);
                          const isSkipped = c.status === "skipped";
                          return (
                            <div
                              key={c.id}
                              className={`flex items-center gap-3 p-2.5 rounded-lg hover:bg-cream/80 transition-colors cursor-pointer ${isSkipped ? "opacity-50" : ""}`}
                              onClick={() => setDetailCelebration(c)}
                            >
                              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-navy text-xs font-bold text-white">
                                {c.member ? getInitials(c.member.name) : "?"}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-semibold text-navy truncate">
                                  {c.member?.name ?? "Unknown"}
                                </p>
                                {/* Plain-text occasion label, no emoji, so
                                    the popover matches the plain-badge
                                    treatment used by the list card and the
                                    detail sheet (per CLAUDE.md: no party
                                    emojis in UI). MS106: handles welcome/
                                    farewell/milestone instead of silently
                                    labeling them as "Work Anniversary".
                                    Years-prefix only applies on the
                                    work_anniversary branch (other types
                                    don't compute anniversaryYears). */}
                                <p className="text-xs text-charcoal/60">
                                  {c.type === "birthday" ? "Birthday"
                                    : c.type === "work_anniversary" ? `${years != null && years > 0 ? ordinalSuffix(years) + " " : ""}Work Anniversary`
                                    : c.type === "welcome" ? "First Day"
                                    : c.type === "farewell" ? "Farewell"
                                    : c.type === "milestone" ? "Milestone"
                                    : "Celebration"}
                                </p>
                              </div>
                              <Badge
                                variant={
                                  // Sprint 2.5a /calendar-fix, Approved
                                  // → navy to align with the list-card
                                  // badge (avoids coral collision with
                                  // urgent states elsewhere).
                                  c.status === "approved" ? "navy" : c.status === "skipped" ? "outline" : "sky"
                                }
                                className="shrink-0 text-[11px]"
                              >
                                {c.status.charAt(0).toUpperCase() + c.status.slice(1)}
                              </Badge>
                            </div>
                          );
                        })}
                      </div>
                      {(() => {
                        // This action approves a single celebration: the
                        // first upcoming one on the day. Name the person in
                        // the label so a buyer on a multi-celebration day
                        // isn't misled into thinking it approves everyone.
                        // The per-row buttons in the detail sheet handle the
                        // others.
                        const firstUpcoming = dayCels.find((c) => c.status === "upcoming");
                        if (!firstUpcoming) return null;
                        const firstName = firstUpcoming.member
                          ? firstUpcoming.member.name.split(" ")[0]
                          : "this celebration";
                        return (
                          <div className="border-t border-charcoal/10 px-3 py-2.5">
                            <Button
                              variant="primary"
                              size="sm"
                              className="w-full text-xs"
                              onClick={() => updateStatus(firstUpcoming, "approved")}
                            >
                              {`Approve ${firstName} and create order`}
                            </Button>
                          </div>
                        );
                      })()}
                    </PopoverContent>
                  </Popover>
                );
              }

              return (
                <div key={key} onClick={() => setSelectedDay(day)}>
                  {cellContent}
                </div>
              );
            })}
          </div>
        </TabsContent>

        {/* ---- LIST VIEW ---- */}
        <TabsContent value="list">
          {filteredCelebrations.length === 0 ? (
            // MS38: editorial empty state for the filtered "no
            // results" case (the team-empty fallback higher up
            // already handles the truly-empty case). Gate on the
            // FILTERED list, not the full one — otherwise a filter
            // that matches nothing rendered an empty space instead
            // of this state (the list maps groupedByMonth, which is
            // built from filteredCelebrations).
            <div className="text-center py-12" role="status" aria-live="polite">
              <p
                style={{
                  fontFamily: "Newsreader, Georgia, serif",
                  fontStyle: "italic",
                  fontSize: 18,
                  color: "rgba(29,52,97,0.75)",
                  margin: 0,
                }}
              >
                Nothing on the list for this filter.
              </p>
              <p className="mt-2 text-sm text-charcoal/55">
                Try a different filter, or skip ahead to the calendar view.
              </p>
            </div>
          ) : (
            <div className="space-y-10">
              {groupedByMonth.map(([month, cels]) => {
                // MS38: month group header in Heidi system. Mono
                // eyebrow with the count + serif label so the list
                // reads like a curated index, not a raw timeline.
                const count = cels.length;
                return (
                  <div key={month}>
                    <header className="mb-4">
                      <div
                        style={{
                          fontFamily:
                            "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace",
                          fontSize: 10,
                          fontWeight: 500,
                          letterSpacing: "0.18em",
                          textTransform: "uppercase",
                          color: "rgba(196,86,74,0.85)",
                          marginBottom: 6,
                        }}
                      >
                        {count} {count === 1 ? "celebration" : "celebrations"}
                      </div>
                      <h3
                        style={{
                          fontFamily: "Newsreader, Georgia, serif",
                          fontWeight: 500,
                          fontSize: "1.5rem",
                          lineHeight: 1.15,
                          letterSpacing: "-0.012em",
                          color: "#1D3461",
                          margin: 0,
                        }}
                      >
                        {month}
                      </h3>
                    </header>
                    <div className="space-y-3">
                      {cels.map((c) => renderCelebrationCard(c))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Detail sheet */}
      <Sheet open={!!detailCelebration} onOpenChange={(open) => !open && setDetailCelebration(null)}>
        <SheetContent className="overflow-y-auto">
          {detailCelebration && (
            <>
              <SheetHeader>
                {/* MS38: Heidi-style sheet header — mono eyebrow + Newsreader
                    italic-emphasis serif title, matching the editorial
                    pattern used across the rest of the authed app. */}
                <div
                  style={{
                    fontFamily:
                      "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace",
                    fontSize: 10,
                    fontWeight: 500,
                    letterSpacing: "0.18em",
                    textTransform: "uppercase",
                    color: "rgba(196,86,74,0.85)",
                    marginBottom: 6,
                  }}
                >
                  {/* MS408: was a binary birthday/work-anniversary ternary
                      that labelled welcome, farewell, milestone, etc. as
                      "Work anniversary". Reuse the same celebrationLabel +
                      capitalize helpers used everywhere else in this file. */}
                  {`${capitalize(celebrationLabel(detailCelebration.type))} · Recipient`}
                </div>
                <SheetTitle asChild>
                  <h2
                    style={{
                      fontFamily: "Newsreader, Georgia, serif",
                      fontWeight: 500,
                      fontSize: "clamp(1.4rem, 2.4vw, 1.75rem)",
                      lineHeight: 1.18,
                      letterSpacing: "-0.012em",
                      color: "#1D3461",
                      margin: 0,
                    }}
                  >
                    Celebration{" "}
                    <em style={{ fontStyle: "italic", color: "#C4564A" }}>details</em>
                  </h2>
                </SheetTitle>
                <SheetDescription className="text-charcoal/65">
                  Approve, skip, or open the order on the right.
                </SheetDescription>
              </SheetHeader>
              <div className="mt-6 space-y-4">
                <div className="flex items-center gap-3">
                  <div className="flex h-14 w-14 items-center justify-center rounded-full bg-navy text-lg font-bold text-white">
                    {detailCelebration.member ? getInitials(detailCelebration.member.name) : "?"}
                  </div>
                  <div>
                    <p className="text-lg font-bold text-navy">{detailCelebration.member?.name}</p>
                    {/* MS105: same celebration-label fix as the inline
                        card badge above. */}
                    <Badge variant={detailCelebration.type === "birthday" ? "coral" : "sky"}>
                      {capitalize(celebrationLabel(detailCelebration.type))}
                    </Badge>
                  </div>
                </div>

                <div>
                  <p className="text-sm text-charcoal/60 font-medium">Date</p>
                  <p className="text-navy">
                    {safeFormat(detailCelebration.date, "EEEE, d MMMM yyyy", "Date not set")}
                  </p>
                  {(() => {
                    const years = anniversaryYears(detailCelebration);
                    return years != null && years > 0 ? (
                      <p className="text-charcoal/70 text-sm">{ordinalSuffix(years)} work anniversary</p>
                    ) : null;
                  })()}
                </div>

                {detailCelebration.member?.dietary_requirements &&
                  detailCelebration.member.dietary_requirements.length > 0 && (
                    <div>
                      <p className="text-sm text-charcoal/60 font-medium mb-1">Dietary Requirements</p>
                      <div className="flex flex-wrap gap-1">
                        {detailCelebration.member.dietary_requirements.map((d) => (
                          <Badge key={d} variant="outline">{d}</Badge>
                        ))}
                      </div>
                    </div>
                  )}

                <div>
                  <p className="text-sm text-charcoal/60 font-medium mb-1">Status</p>
                  <Badge
                    variant={
                      // Sprint 2.5a /calendar-fix, see badge note in
                      // renderCelebrationCard. Approved → navy to keep
                      // the visual semantic consistent across all
                      // three badge sites (list card, popover, sheet).
                      detailCelebration.status === "approved"
                        ? "navy"
                        : detailCelebration.status === "skipped"
                        ? "outline"
                        : "sky"
                    }
                  >
                    {detailCelebration.status.charAt(0).toUpperCase() + detailCelebration.status.slice(1)}
                  </Badge>
                </div>

                {detailCelebration.status === "upcoming" && (
                  <div className="flex gap-2 pt-2">
                    <Button variant="primary" onClick={() => updateStatus(detailCelebration, "approved")}>
                      Approve
                    </Button>
                    <Button variant="outline" onClick={() => updateStatus(detailCelebration, "skipped")}>
                      Skip
                    </Button>
                  </div>
                )}

                {detailCelebration.status === "approved" && (
                  <Button
                    variant="primary"
                    className="w-full mt-2"
                    onClick={() => {
                      // MS65: same fix as the inline CelebrationItem button
                      // above — route through celebrationToChatUrl rather
                      // than the dropped ?celebration_id param.
                      if (detailCelebration.member) {
                        openFromChatUrl(
                          celebrationToChatUrl(detailCelebration, detailCelebration.member),
                        );
                      } else {
                        openGeneric();
                      }
                    }}
                  >
                    Create order →
                  </Button>
                )}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </PageShell>
  );
}
